import { _ } from "~/i18n";
import { getPremiumIconSrc, node } from "~/util/dom";
import FandaCategory from "./fandaCategory";
import { IFandaMe } from "~/util/providers/premium";
import { store } from "~/state/store";
import { IUserInfo } from "~/state/user/interfaces";
import Tooltip from "../tooltip";

function isDateWithinNDaysOfAnotherDate(dateToCheck: Date, today: Date, days: number): boolean {
	const sixtyDaysFromNow = new Date(today.getTime() + (days * 24 * 60 * 60 * 1000));

	return dateToCheck <= sixtyDaysFromNow;
}

export default class FandaStatus {
	#container: HTMLDivElement;

	#countPhotos: FandaCategory;

	#countReviews: FandaCategory;

	#countTrafficReports: FandaCategory;

	#countDistanceDriven: FandaCategory;

	#countPlaces: FandaCategory;

	#countDataReports: FandaCategory;

	#progressBar: HTMLDivElement;

	#progressDone: HTMLDivElement;

	#progressToDo: HTMLDivElement;

	#progressFanda: HTMLDivElement;

	#progressMarker: HTMLDivElement;

	#avatar: HTMLMapyAvatarElement;

	#title: HTMLHeadingElement;

	#subtitle: HTMLParagraphElement;

	#textBubble: HTMLDivElement;

	#categoriesTitle: HTMLHeadingElement;

	#categoriesContainer: HTMLDivElement;

	#fandaThankYou: HTMLDivElement;

	constructor() {
		const { user } = store.getState().user;

		this.#container = node("div", { className: "ui-fandaStatus" });

		// nadpis
		this.#title = node("h2", { className: "ui-fandaStatus__title" }, _("mymap.fandaStatus.titleTraveler"));
		this.#subtitle = node("p", { className: "ui-fandaStatus__subtitle", innerHTML: _("mymap.fandaStatus.subtitleTraveler", { start: "<strong>", end: "</strong>" }) });

		// ilustrace
		const illustration = node("img", { src: "/img/mymap/fanda.svg", className: "ui-fandaStatus__illustration", crossOrigin: "anonymous" });

		// progress bar
		this.#avatar = node("mapy-avatar", { size: 24, className: "ui-fandaStatus__progress__marker__avatar" });
		user.avatar && this.#avatar.appendChild(node("img", { src: user.avatar, srcset: `${user.avatar}/24 1x, ${user.avatar}/48 2x`, crossOrigin: "anonymous" }));
		this.#progressBar = node("div", { className: "ui-fandaStatus__progress" });
		this.#progressDone = node("div", { className: "ui-fandaStatus__progress__done" });
		this.#progressToDo = node("div", { className: "ui-fandaStatus__progress__toDo" });
		this.#progressFanda = node("div", { className: "ui-fandaStatus__progress__fanda" });
		this.#progressMarker = node("div", { className: "ui-fandaStatus__progress__marker" });
		this.#progressMarker.append(
			node("img", { src: "/img/mymap/fanda-shadow.png", className: "ui-fandaStatus__progress__marker__shadow", crossOrigin: "anonymous" }),
			node("img", { src: "/img/mymap/fanda-drop.svg", className: "ui-fandaStatus__progress__marker__drop", crossOrigin: "anonymous" }),
			this.#avatar,
		);
		this.#progressBar.append(this.#progressToDo, this.#progressFanda, this.#progressMarker);

		// kategorie
		this.#categoriesTitle = node("h2", { className: "ui-fandaStatus__categories__title" }, _("mymap.fandaStatus.categoriesTitle"));
		this.#fandaThankYou = node("div", { className: "ui-fandaStatus__thankYou" });
		this.#fandaThankYou.append(node("p", {}, _("mymap.fandaStatus.thankYou")), node("img", { src: "/img/mymap/fanda-heart.svg", crossOrigin: "anonymous" }));
		this.#categoriesContainer = node("div", { className: "ui-fandaStatus__categories" });

		this.#countPhotos = new FandaCategory(_("mymap.fandaStatus.category.photos"), 0);
		this.#countReviews = new FandaCategory(_("mymap.fandaStatus.category.reviews"), 0);
		this.#countTrafficReports = new FandaCategory(_("mymap.fandaStatus.category.trafficReports"), 0);
		this.#countDistanceDriven = new FandaCategory(_("mymap.fandaStatus.category.distanceDriven"), 0);
		this.#countPlaces = new FandaCategory(_("mymap.fandaStatus.category.places"), 0);
		this.#countDataReports = new FandaCategory(_("mymap.fandaStatus.category.dataReports"), 0);
		this.#categoriesContainer.append(this.#countPhotos.container, this.#countReviews.container, this.#countTrafficReports.container, this.#countDistanceDriven.container, this.#countPlaces.container, this.#countDataReports.container);

		// tooltipy
		[
			{
				category: this.#countPhotos,
				text: _("mymap.fandaStatus.tooltip.photos"),
			},
			{
				category: this.#countReviews,
				text: _("mymap.fandaStatus.tooltip.reviews"),
			},
			{
				category: this.#countTrafficReports,
				text: _("mymap.fandaStatus.tooltip.trafficReports"),
			},
			{
				category: this.#countDistanceDriven,
				text: _("mymap.fandaStatus.tooltip.distanceDriven"),
			},
			{
				category: this.#countPlaces,
				text: _("mymap.fandaStatus.tooltip.places"),
			},
			{
				category: this.#countDataReports,
				text: _("mymap.fandaStatus.tooltip.dataReports"),
			},
		].forEach(item => {
			// eslint-disable-next-line no-new
			new Tooltip({
				anchor: item.category.container,
				trigger: item.category.container,
				content: node("p", {}, item.text),
				hideTimeout: 0,
				width: 232,
			});
		});

		// sestaveni DOMu
		this.#container.append(this.#title, this.#subtitle, illustration, this.#progressBar, this.#categoriesTitle, this.#categoriesContainer);

		// text v zelene bubline
		this.#textBubble = node("div", { className: "ui-fandaStatus__textBubble" });
		this.#setTextBubble(user);
	}

	get container() {
		return this.#container;
	}

	setData(data: IFandaMe, user: IUserInfo) {
		// update markeru uzivatele
		this.#avatar.innerHTML = "";
		user.avatar && this.#avatar.appendChild(node("img", { src: user.avatar, srcset: `${user.avatar}/24 1x, ${user.avatar}/48 2x`, crossOrigin: "anonymous" }));

		// cisilka
		this.#countPhotos.count = data?.user?.totalPhotos;
		this.#countReviews.count = data?.user?.totalReviews;
		this.#countTrafficReports.count = data?.user?.totalTrafficFeedbacks;
		this.#countDistanceDriven.count = data?.user?.totalKilometers;
		this.#countPlaces.count = data?.user?.totalUserPoiCreated;
		this.#countDataReports.count = data?.user?.totalUserPoiUpdated;

		// progress
		this.#progressBar.style.setProperty("--fanda-progress", `${data?.user?.progressActual}%`);
		this.#progressBar.style.setProperty("--fanda-rest", `${100 - data?.user?.progressBoundary}%`);
		this.#progressBar.style.setProperty("--fanda-max", `${Math.min(data?.user?.progressActual, data?.user?.progressBoundary)}%`);

		// upravy podle toho, zda jsem nebo nejsem fanda
		// jsem fanda
		if (data?.user?.isFanda) {
			// progress bar
			this.#progressToDo.remove();

			// nadpis
			this.#title.textContent = _("mymap.fandaStatus.titleExplorer");
			this.#subtitle.innerHTML = "&nbsp;";

			// nad kategoriemi
			this.#categoriesTitle.remove();
			this.#categoriesContainer.parentNode.insertBefore(this.#fandaThankYou, this.#categoriesContainer);
		// nejsem fanda
		} else {
			// progress bar
			this.#progressBar.prepend(this.#progressToDo);

			// nadpis
			this.#title.textContent = _("mymap.fandaStatus.titleTraveler");
			this.#subtitle.innerHTML = _("mymap.fandaStatus.subtitleTraveler", { start: "<strong>", end: "</strong>" });

			// nad kategoriemi
			this.#fandaThankYou.remove();
			this.#categoriesContainer.parentNode.insertBefore(this.#categoriesTitle, this.#categoriesContainer);
		}

		if (data?.user?.progressActual > 0) {
			this.#progressBar.prepend(this.#progressDone);
		} else {
			this.#progressDone.remove();
		}

		// texty
		this.#setTextBubble(user, data);
	}

	#setTextBubble(user: IUserInfo, data?: IFandaMe) {
		this.#textBubble.innerHTML = "";
		this.#progressBar.parentNode?.insertBefore(this.#textBubble, this.#progressBar.nextSibling);

		// mam nejakou formu premia
		if (user.premiumData.subscriptions?.length) {
			const logo = `<img src="${getPremiumIconSrc()}" crossorigin="anonymous" />`;

			// platim
			if (user.premiumData.subscriptions[0].isPaid) {
				// zbyva mene nez dva mesice do konce a ja to nemam opakovaci
				if (!user.premiumData.subscriptions[0].isRecurring && user.premiumData.subscriptions[0].expireDate && isDateWithinNDaysOfAnotherDate(new Date(user.premiumData.subscriptions[0].expireDate), new Date(), 60)) {
					// dosahnu na freemium
					if (data?.user?.isFanda) {
						this.#textBubble.innerHTML = _("mymap.fandaStatus.greenTextPaidExpiring", { start: `<span>`, end: `</span>`, logo });
					// nedosahnu na freemium
					} else {
						this.#textBubble.innerHTML = _("mymap.fandaStatus.greenTextBecomeFan", { start: `<span>`, end: `</span>`, logo });
					}
				// platim a bud mam zaplaceno jeste dlouho, nebo se to ma samo obnovit
				} else {
					this.#textBubble.remove();
				}
			// neplatim, mam fandu, ale brzy vyprsi
			} else if (user.premiumData.subscriptions[0].expireDate && isDateWithinNDaysOfAnotherDate(new Date(user.premiumData.subscriptions[0].expireDate), new Date(), 60)) {
				this.#textBubble.innerHTML = _("mymap.fandaStatus.greenTextFreeExpiring", { start: `<span>`, end: `</span>`, logo });
			// mam fandu a do konce je daleko
			} else if (data?.user?.isFanda) {
				this.#textBubble.innerHTML = _("mymap.fandaStatus.greenTextFanUnlocked", { start: `<span>`, end: `</span>`, logo });
			// nemam ani fandu
			} else {
				this.#textBubble.remove();
			}
		} else {
			this.#textBubble.remove();
		}
	}
}
